/**
 * Icons used by the Workspace Media UI.
 *
 * Prefer the project's existing icon set under `src/components/icons/`.
 * Each re-export is a thin wrapper that flips the project's default
 * `color="var(--primary-text-color)"` to `color="currentColor"` so the
 * icon inherits from button/text/theme context — that's the contract
 * the rest of this folder relies on.
 *
 * A handful of icons aren't in the project set (chevron-left, select-all,
 * deselect, delete-forever, file-type variants, etc.) so they live as
 * local inline SVGs at the bottom of the file.
 */
import React from 'react';

/* ─── Re-exports from the project icon library ─── */
import Close from '../../icons/Close';
import ChevronRight from '../../icons/ChevronRight';
import Search from '../../icons/Search';
import ArrowRightAlt from '../../icons/ArrowRightAlt';
import OpenInNew from '../../icons/OpenInNew';
import Download from '../../icons/Download';
import Refresh from '../../icons/Refresh';
import Delete from '../../icons/Delete';
import Unarchive from '../../icons/Unarchive';
import Schedule from '../../icons/Schedule';
import GridView from '../../icons/GridView';
import ListAlt from '../../icons/ListAlt';
import ProgressActivity from '../../icons/ProgressActivity';
import IconDescriptionProj from '../../icons/IconDescription';
import ImageIcon from '../../icons/Image';
import Warning from '../../icons/Warning';

const adapt = (Component) => {
  /*
   * Project icons accept (size, color) but don't pass `className` through
   * to the underlying <svg>. Render the icon bare in the common case; only
   * fall back to a flex-span wrapper when a className is supplied (needed
   * for spinner rotation via `.wm__spin`, etc.). This keeps the DOM clean
   * for the 95% of icons that don't need a class.
   */
  const Wrapped = ({ color = 'currentColor', className, size = 16, ...rest }) => {
    if (!className) return <Component color={color} size={size} {...rest} />;
    return (
      <span
        className={className}
        style={{ display: 'inline-flex', lineHeight: 0, width: size, height: size }}
      >
        <Component color={color} size={size} {...rest} />
      </span>
    );
  };
  Wrapped.displayName = `Adapted(${Component.displayName || Component.name || 'Icon'})`;
  return Wrapped;
};

export const IconClose = adapt(Close);
export const IconChevronRight = adapt(ChevronRight);
export const IconSearch = adapt(Search);
export const IconArrowForward = adapt(ArrowRightAlt);
export const IconOpenInNew = adapt(OpenInNew);
export const IconDownload = adapt(Download);
export const IconRefresh = adapt(Refresh);
export const IconDelete = adapt(Delete);
export const IconRestore = adapt(Unarchive);
export const IconSchedule = adapt(Schedule);
export const IconGridView = adapt(GridView);
export const IconViewList = adapt(ListAlt);
export const IconSpinner = adapt(ProgressActivity);
export const IconDescription = adapt(IconDescriptionProj);
export const IconImage = adapt(ImageIcon);
export const IconWarning = adapt(Warning);

/* ─── Local SVGs for icons that aren't in the project library ───
   Same viewBox + filled style as the project's Material Symbols paths
   so they look at home next to the re-exports. */
const local = (d, viewBox = '0 -960 960 960') => ({ size = 18, color = 'currentColor', className = '', ...rest }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox={viewBox}
    fill={color}
    aria-hidden="true"
    className={className}
    {...rest}
  >
    <path d={d} />
  </svg>
);

export const IconChevronLeft = local('M560-240 320-480l240-240 56 56-184 184 184 184-56 56Z');
export const IconDeleteForever = local('M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z M375-272 480-377l103 105 53-52-104-104 104-104-53-52-103 105-104-105-53 52 104 104-104 104 53 52Z');
export const IconSelectAll = local('M200-120q-33 0-56.5-23.5T120-200h80v80Zm-80-160v-80h80v80h-80Zm0-160v-80h80v80h-80Zm0-160v-80h80v80h-80Zm0-160q0-33 23.5-56.5T200-840v80h-80Zm240 640v-80h80v80h-80ZM280-200v-560h560v560H280Zm0-640v-80h80v80h-80Zm160 640v-80h80v80h-80Zm0-640v-80h80v80h-80Zm160 640v-80h80v80h-80Zm0-640v-80h80v80h-80Zm160 640v-80h80v80h-80Zm0-640v-80h80v80h-80Zm80 160h-80v-80h80v80Zm0 160h-80v-80h80v80Zm0 160h-80v-80h80v80Zm0 160h-80v-80q33 0 56.5 23.5T840-200ZM360-280h240l-78-104-62 80-40-50-60 74Z');
export const IconDeselect = local('M819-28 27-820l51-51L870-79l-51 51ZM800-296l-80-80v-264H536l-80-80h344q33 0 56.5 23.5T880-640v320q0 8-2 14.5t-2 14.5l-76-5ZM440-440Zm78-186-58-58 53-66 50 64 105-134h-26v-80h160v80h-30L518-626ZM314-200h298l-80-80h-58v-58l-80-80h-50l-30 38v180Zm-80 80v-280q-1 0-1 .5t-2 .5l-50-50q-3-9-4-16t-1-15v-320q0-15 4.5-26.5T194-849L60-983l57-57 793 793-57 57-91-91v161H234Zm164-360Z');
export const IconFolderOpen = local('M160-160q-33 0-56.5-23.5T80-240v-480q0-33 23.5-56.5T160-800h240l80 80h320q33 0 56.5 23.5T880-640H447l-80-80H160v480l96-320h684L837-217q-8 26-29.5 41.5T760-160H160Zm84-80h516l72-240H316l-72 240Zm0 0 72-240-72 240Zm-84-400v-80 80Z');
export const IconCopyAll = local('M360-240q-33 0-56.5-23.5T280-320v-480q0-33 23.5-56.5T360-880h360q33 0 56.5 23.5T800-800v480q0 33-23.5 56.5T720-240H360Zm0-80h360v-480H360v480ZM200-80q-33 0-56.5-23.5T120-160v-560h80v560h440v80H200Zm160-720v480-480Z');

/* File-type icons (project library doesn't differentiate these) */
export const IconPdf = local('M331-431h37v-83h45q15.725 0 26.863-11.137Q451-536.275 451-552v-46q0-15.725-11.137-26.863Q428.725-636 413-636h-82v205Zm37-120v-48h45v48h-45Zm129 120h83q14.45 0 25.725-11.137Q617-453.275 617-468v-130q0-15.725-11.275-26.863Q594.45-636 580-636h-83v205Zm37-37v-131h46v131h-46Zm133 37h37v-83h50v-37h-50v-48h50v-37h-87v205ZM320-240q-33 0-56.5-23.5T240-320v-480q0-33 23.5-56.5T320-880h480q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240H320ZM160-80q-33 0-56.5-23.5T80-160v-560h80v560h560v80H160Z');
export const IconVideo = local('M280-120q-33 0-56.5-23.5T200-200v-560q0-33 23.5-56.5T280-840h280l240 240v440q0 33-23.5 56.5T720-120H280Zm280-560v-160H280v560h440v-400H560ZM280-840v160-160 560-560Z M380-280h280l-94-127-70 94-50-67-66 100Z');
export const IconAudio = local('M400-120q-66 0-113-47t-47-113q0-66 47-113t113-47q23 0 42.5 5.5T480-418v-422h240v160H560v400q0 66-47 113t-113 47Z');
export const IconArchive = local('M200-120q-33 0-56.5-23.5T120-200v-499q0-14 4.5-27t13.5-24l50-61q11-14 27.5-21.5T250-840h460q18 0 34.5 7.5T772-811l50 61q9 11 13.5 24t4.5 27v499q0 33-23.5 56.5T760-120H200Zm16-600h528l-34-40H250l-34 40Zm-16 80v440h560v-440H200Zm280 360 160-160-56-56-64 64v-168h-80v168l-64-64-56 56 160 160ZM200-200h560-560Z');
export const IconSheet = local('M120-120v-680q0-33 23.5-56.5T200-880h560q33 0 56.5 23.5T840-800v680H120Zm80-560h560v-120H200v120Zm0 80v120h120v-120H200Zm200 0v120h160v-120H400Zm240 0v120h120v-120H640ZM200-280v120h120v-120H200Zm200 0v120h160v-120H400Zm240 0v120h120v-120H640ZM200-480v120h120v-120H200Zm200 0v120h160v-120H400Zm240 0v120h120v-120H640Z');
export const IconSlides = local('M160-200v-640h680v640H160Zm80-80h520v-480H240v480Zm120-60h280v-360H360v360Z');
export const IconNotes = local('M120-280v-80h360v80H120Zm0-160v-80h560v80H120Zm0-160v-80h560v80H120Zm480 320v-80h240v80H600Z');
export const IconFile = local('M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Zm280-560v-160H240v640h480v-480H520ZM240-800v160-160 640-640Z');
